import React from 'react';
import { cacheExchange, createClient, Exchange, fetchExchange, Operation, Provider } from 'urql';
import { fromPromise, fromValue, map, mergeMap, pipe } from 'wonka';
import { useAuth0 } from '@auth0/auth0-react';

type Props = { children: React.ReactElement };

export const Auth0UrqlProvider = ({ children }: Props) => {
  const { getIdTokenClaims } = useAuth0();

  const fetchOptionsExchange =
    (fn: any): Exchange =>
    ({ forward }) =>
    (ops$) => {
      return pipe(
        ops$,
        mergeMap((operation: Operation) => {
          const result = fn(operation.context.fetchOptions);
          return pipe(
            (typeof result.then === 'function' ? fromPromise(result) : fromValue(result)) as any,
            map((fetchOptions: RequestInit | (() => RequestInit)) => ({
              ...operation,
              context: { ...operation.context, fetchOptions },
            })),
          );
        }),
        forward,
      );
    };

  const client = createClient({
    url: import.meta.env.VITE_GRAPHQL_ENDOPOINT || 'https://stg.api.giho.jp/graphql',
    requestPolicy: 'cache-and-network',
    suspense: true,
    exchanges: [
      cacheExchange,
      fetchOptionsExchange(async (fetchOptions: any) => {
        const token = await getIdTokenClaims();

        return Promise.resolve({
          ...fetchOptions,
          headers: {
            Authorization: token ? `Bearer ${token.__raw}` : '',
          },
        });
      }),
      fetchExchange,
    ],
  });

  return <Provider value={client}>{children}</Provider>;
};
